/* eslint-disable react/no-danger */
import _, { isEmpty } from 'lodash';
import React, { useContext, useState } from 'react';
import { motion } from 'framer-motion';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { Link } from 'gatsby';
import BoxedContainer from './layouts/BoxedContainer';
import Sprites from '../components/Sprites';
import { getTranslationByKey } from '../helpers/translation-helper';
import { DWTCContext } from '../utils/context';

export default function FreeZoneInnerPageBanner({ id, data }) {
  const { title, text, image, mobileImage, links } = data;
  const [dropdownVisible, setDropdownVisible] = useState(false);

  const toggleDropdown = () => {
    setDropdownVisible(!dropdownVisible);
  };

  const smoothScrollTo = (targetId) => {
    const targetElement = document.getElementById(targetId);
    if (targetElement) {
      const scrollPosition = targetElement.offsetTop - 100;
      window.scrollTo({
        top: scrollPosition,
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const { translations } = useContext(DWTCContext);

  return (
    <section
      id={id}
      className={`2l:mt-[142px] relative overflow-hidden flex w-full items-center justify-center
      ${!_.isEmpty(links) ? 'min-h-[290px] md:min-h-[450px]' : 'min-h-[242px] md:min-h-[370px]'}
      `}>
      {isEmpty(image) || (
        <LazyLoadImage
          src={image?.url}
          width="100%"
          effect="blur"
          placeholderSrc={`${image?.url}`}
          className={`w-full h-full object-cover
          ${!_.isEmpty(links) ? 'min-h-[450px]' : 'min-h-[370px]'}
          `}
          wrapperClassName={`absolute z-5 top-0 bottom-0
          ${mobileImage !== null ? '!hidden md:!block' : ''}`}
        />
      )}
      {isEmpty(mobileImage) || (
        <LazyLoadImage
          src={mobileImage?.url}
          width="100%"
          effect="blur"
          placeholderSrc={`${mobileImage?.url}`}
          className={`w-full h-auto object-cover
          ${!_.isEmpty(links)
            ? 'min-h-[290px]'
            : 'min-h-[240px] h-full'}
          `}
          wrapperClassName="md:!hidden absolute z-5 top-0 bottom-0"
        />
      )}
      <div
        className="absolute top-0 w-full h-full before:!inline before:content-['']
      before:absolute before:w-full before:h-full before:bg-[#1a1a1a] before:bg-opacity-50"
      />
      <div
        className="relative z-10 flex flex-col h-full items-center justify-center
      min-h-[242px] md:min-h-[370px] py-8 md:py-12">
        <BoxedContainer className="flex justify-center 1xl:max-w-[1300px]">
          <div className="flex flex-col gap-5 md:gap-10 justify-center">
            <div className="flex flex-col gap-5 md:gap-8 items-center">
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.4 }}
                viewport={{ once: true }}
                dangerouslySetInnerHTML={{ __html: title }}
                className="pt-15 md:pt-0 text-white text-center text-clamp20to60 leading-1.2
                font-normal h1-strong:font-bold
                ltr:font-URWDINMedium rtl:font-GESSTwoDWTCMedium
                h1-strong:ltr:font-URWDINBold h1-strong:rtl:font-GESSTwoDWTCMedium
                w-full max-w-[1100px] h1-child:w-full"
              />
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.5 }}
                viewport={{ once: true }}
                dangerouslySetInnerHTML={{ __html: text }}
                className="p-child:text-white p-child:text-center p-child:ltr:text-clamp10to20 p-child:rtl:text-clamp12to20
                p-child:font-normal p-child:leading-[1.6] p-child:mb-0 p-child:font-LibreBaskervilleRegular max-w-[970px]"
              />
            </div>
            {_.isEmpty(links) || (
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.6 }}
                viewport={{ once: true }}
                className="hidden md:flex flex-wrap gap-4 lg:gap-[20px] justify-center">
                {_.map(links, (link, index) => (
                  <button
                    key={index}
                    type="button"
                    className="flex justify-center items-center bg-[#E5E5E5]
                    hover:bg-[#B53B50] hover:text-white transition-[.3s]
                    leading-1.25 text-[11px] md:text-[15px] px-8 rtl:pt-5 ltr:pt-[22px] pb-[18px] h-[53px]
                    ltr:font-URWDINMedium rtl:font-GESSTwoDWTCMedium text-[#4D4D4D] rounded-full"
                    onClick={() => smoothScrollTo(link?.url?.split('#')[1])}>
                    <span>{link?.name}</span>
                  </button>
                ))}
              </motion.div>
            )}
            {_.isEmpty(links) || (
              <div className="flex md:hidden flex-col flex-wrap justify-center relative">
                <button
                  onClick={toggleDropdown}
                  type="button"
                  className={`bg-[#E5E5E5] pb-3 pt-[13px] lg:pt-[19px] lg:pb-5 px-4 lg:px-7 
                    text-[14px]
                  lg:text-[18px] leading-1.5 h-10
                  ltr:font-URWDINMedium rtl:font-GESSTwoDWTCMedium text-[#4D4D4D] rounded-[25px]
                  flex flex-row justify-center gap-[15px] items-center
                  ${dropdownVisible ? 'rounded-b-[0]' : ''}
                  `}>
                  <span>{getTranslationByKey(translations,'Browse Sections')}</span>
                  <Sprites
                    name={`${dropdownVisible ? 'free-zone-arrow-up' : 'free-zone-arrow-down'}`}
                  />
                </button>
                <div className={`${dropdownVisible ? '' : 'hidden'} bg-[#E5E5E5] rounded-b-[25px]`}>
                  <div className="flex flex-col justify-center pb-3">
                    {links.map((link, index) => (
                      <Link
                        key={index}
                        to={link?.url}
                        className="bg-[#E5E5E5] py-2 px-3.5 text-[15px]
                        lg:text-[18px] leading-1
                        ltr:font-URWDINMedium rtl:font-GESSTwoDWTCMedium text-[#4D4D4D] text-center
                        last:rounded-b-full">
                        {link?.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </BoxedContainer>
      </div>
    </section>
  );
}
